import { ReactNode, useEffect, useRef } from "react";
import { Box, Typography, Alert, Button } from "@mui/material";
import { Family } from "protogen/advisors_service_pb";
import {
  useListFamilyAnnotations,
  useUpdateFamilyAnnotation,
} from "../../services/engagement";
import Loading from "../common/Loading";
import { MessageSquare } from "lucide-react";
import { useListInvoices } from "../../services/billing";
import { ListInvoicesRequest } from "../../protogen/billing_service_pb";
import LinkRouter from "../navigation/LinkRouter";
import { Advisor } from "protogen/common_pb";
import AnnotationCard from "components/engagement/notes/AnnotationCard";
import InvoiceCard from "components/engagement/notes/InvoiceCard";
import AutomatedFlagsCard from "components/engagement/notes/AutomatedFlagsCard";
import AddAnnotation, {
  AddAnnotationRef,
} from "components/engagement/notes/AddAnnotation";
import ChangeEventCard from "components/engagement/notes/ChangeEventCard";
import { CHECKIN_FLAGS } from "components/engagement/flags";
import { LEGACY_CHECKIN_DATE } from "components/engagement/Filters";
import { PanelProps } from "components/details-page/TabbedDetailsPanel";

const TYPE_OPTIONS = [
  "note",
  "billing",
  "client-feedback",
  "10-day-checkin",
  "30-day-checkin",
  "50-day-checkin",
];

interface Props extends PanelProps {
  title: string;
  family: Family;
  primaryAdvisor: Advisor;
}

const CheckInMissing = ({
  startDate,
  annotations,
  daysThreshold,
  checkInType,
  onAction,
}: {
  startDate: string;
  annotations: any[];
  daysThreshold: number;
  checkInType: string;
  onAction: () => void;
}) => {
  // NOTE: Skip families that predate the checkin feature
  if (!startDate || new Date(startDate) < LEGACY_CHECKIN_DATE) return null;

  const startDateMs = new Date(startDate).getTime();
  const now = new Date().getTime();
  const daysSinceStart = (now - startDateMs) / (1000 * 60 * 60 * 24);

  if (daysSinceStart < daysThreshold) return null;

  const missing = !annotations?.some(
    (annotation) => annotation.noteType === checkInType,
  );
  if (!missing) return null;
  return (
    <Alert
      severity="warning"
      action={
        <Button
          variant="text"
          size="small"
          onClick={onAction}
          sx={{
            height: "unset",
            alignSelf: "center",
            marginRight: "12px",
          }}
        >
          Add checkin
        </Button>
      }
    >
      <i>{`#${checkInType}`} is overdue for this family</i>
    </Alert>
  );
};

export default ({ family, primaryAdvisor }: Props) => {
  const editorRef = useRef<AddAnnotationRef>(null);
  const {
    request: listInvoices,
    loading: invoicesLoading,
    data: invoicesResponse,
  } = useListInvoices();
  const {
    request: listAnnotations,
    data: annotationsResponse,
    loading: annotationsLoading,
  } = useListFamilyAnnotations();
  const { request: deleteAnnotation } = useUpdateFamilyAnnotation();
  useEffect(() => {
    listAnnotations({ familyRef: family.ref });
    listInvoices(
      new ListInvoicesRequest({
        familyRef: family.ref,
      }),
    );
  }, [family.ref]);
  const loading =
    (invoicesLoading || annotationsLoading) &&
    !(annotationsResponse && invoicesResponse);
  // Merge results
  const feedEntries: [number, ReactNode][] = [
    ...(annotationsResponse?.annotations || []).map(
      (annotation): [number, ReactNode] => [
        Number(annotation.lastUpdatedSec),
        <AnnotationCard
          key={`annotation-${annotation.ref}`}
          annotation={annotation}
          handleDelete={async () => {
            await deleteAnnotation({
              annotationRef: annotation.ref,
              delete: true,
            });
            await listAnnotations({ familyRef: family.ref });
          }}
        />,
      ],
    ),
    ...(invoicesResponse?.invoices || []).map(
      (invoice): [number, ReactNode] => [
        Number(invoice.createdSec),
        <InvoiceCard key={`invoice-${invoice.ref}`} invoice={invoice} />,
      ],
    ),
    ...(annotationsResponse?.changeEvents || []).map(
      (annotation): [number, ReactNode] => [
        Number(annotation.lastUpdatedSec),
        <ChangeEventCard
          key={`annotation-${annotation.ref}`}
          annotation={annotation}
        />,
      ],
    ),
  ];
  if (
    annotationsResponse?.automatedFlags &&
    annotationsResponse?.automatedFlags.length > 0
  ) {
    feedEntries.push([
      Number(annotationsResponse?.automatedFlags[0].lastUpdatedSec),
      <AutomatedFlagsCard
        key={"automatedFlags"}
        flags={annotationsResponse.automatedFlags}
      />,
    ]);
  }
  feedEntries.sort((a, b) => b[0] - a[0]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
        alignSelf: "stretch",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"16px"}
        width={"100%"}
      >
        <AddAnnotation
          ref={editorRef}
          familyRef={family.ref}
          signupSessionRef={""}
          onUpdate={async () => {
            await listAnnotations({ familyRef: family.ref });
          }}
          noteTypes={TYPE_OPTIONS}
          includeFlags={true}
          flagOptions={CHECKIN_FLAGS}
        />
      </Box>
      {annotationsResponse && (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={"16px"}
          width={"100%"}
        >
          <CheckInMissing
            startDate={family.startDate}
            annotations={annotationsResponse?.annotations || []}
            daysThreshold={10}
            checkInType={"10-day-checkin"}
            onAction={() => editorRef.current?.changeNoteType("10-day-checkin")}
          />
          <CheckInMissing
            startDate={family.startDate}
            annotations={annotationsResponse?.annotations || []}
            daysThreshold={30}
            checkInType={"30-day-checkin"}
            onAction={() => editorRef.current?.changeNoteType("30-day-checkin")}
          />
          <CheckInMissing
            startDate={family.startDate}
            annotations={annotationsResponse?.annotations || []}
            daysThreshold={50}
            checkInType={"50-day-checkin"}
            onAction={() => editorRef.current?.changeNoteType("50-day-checkin")}
          />
        </Box>
      )}
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"16px"}
        width={"100%"}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={"16px"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h3">Activity</Typography>
          <Typography variant="bodySmall" color="textSecondary">
            <LinkRouter
              sx={{ alignItems: "center" }}
              targetNew={true}
              to={`/inbox/messages?recipientRefs=${encodeURIComponent(
                primaryAdvisor.ref,
              )}`}
            >
              <MessageSquare size={12} style={{ marginRight: 4 }} />
              Message {primaryAdvisor.firstName} {primaryAdvisor.lastName}
            </LinkRouter>
          </Typography>
        </Box>
        {loading && <Loading />}
        {feedEntries.map((entry) => entry[1])}
      </Box>
    </Box>
  );
};
